import React, { useState, useEffect } from 'react';
import {
    Box,
    Grid,
    Paper,
    Typography,
    MenuItem,
    Select,
    FormControl,
    IconButton,
    CircularProgress,
    alpha,
    Stack,
    Avatar,
    Fade,
    Grow
} from '@mui/material';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell
} from 'recharts';
import {
    TrendingUp as TrendingIcon,
    Business as BusinessIcon,
    Groups as StudentIcon,
    School as CollegeIcon,
    MoreHoriz as MoreIcon,
    ArrowUpward as ArrowUpIcon,
    CalendarToday as CalendarIcon
} from '@mui/icons-material';

import {
    getDashboardStats,
    getCollegeHiringData,
    getApprenticeshipOJTComparison
} from '../Apis/DashboardApis';

const StatCard = ({ title, count, subtitle, icon, color, delay = 0 }) => {
    return (
        <Grow in={true} timeout={500 + delay}>
            <Paper
                elevation={0}
                sx={{
                    p: 3,
                    borderRadius: 5,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    minHeight: 160,
                    position: 'relative',
                    overflow: 'hidden',
                    border: '1px solid #F1F5F9',
                    background: '#FFFFFF',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.02), 0 10px 15px -3px rgba(0,0,0,0.03)',
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.05)'
                    }
                }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Avatar sx={{
                        bgcolor: alpha(color, 0.1),
                        color: color,
                        width: 48,
                        height: 48,
                        borderRadius: 3
                    }}>
                        {icon}
                    </Avatar>
                    <Box sx={{ display: 'flex', alignItems: 'center', color: '#10B981' }}>
                        <ArrowUpIcon sx={{ fontSize: 16 }} />
                        <Typography variant="caption" fontWeight="bold">12%</Typography>
                    </Box>
                </Box>

                <Box>
                    <Typography variant="h3" fontWeight="1000" sx={{ color: '#0F172A', mb: 0.5, letterSpacing: -1 }}>
                        {count}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ color: '#64748B', fontWeight: '800', textTransform: 'uppercase', fontSize: '0.65rem', letterSpacing: 0.5 }}>
                        {title}
                    </Typography>
                </Box>

                {/* Subtle background decoration */}
                <Box sx={{
                    position: 'absolute',
                    right: -20,
                    bottom: -20,
                    opacity: 0.03,
                    transform: 'rotate(-20deg)'
                }}>
                    {React.cloneElement(icon, { sx: { fontSize: 120 } })}
                </Box>
            </Paper>
        </Grow>
    );
};

const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
        return (
            <Paper sx={{ p: 2, border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', borderRadius: 3 }}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>{label}</Typography>
                {payload.map((entry, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: entry.color }} />
                        <Typography variant="body2" color="textSecondary">
                            {entry.name}: <span style={{ fontWeight: 'bold', color: '#0F172A' }}>{entry.value}</span>
                        </Typography>
                    </Box>
                ))}
            </Paper>
        );
    }
    return null;
};

const Home = () => {
    const [hiringFilter, setHiringFilter] = useState('this-month');
    const [pieFilter] = useState('this-month');
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [barData, setBarData] = useState([]);
    const [pieData, setPieData] = useState([]);

    const fetchStats = async () => {
        try {
            const response = await getDashboardStats('this-month');
            setStats(response.data);
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    };

    const fetchCollegeHiring = async (period) => {
        try {
            const response = await getCollegeHiringData(period, 10);
            setBarData(response.data?.data || []);
        } catch (error) {
            console.error('Error fetching college hiring:', error);
            setBarData([]);
        }
    };

    const fetchComparison = async (period) => {
        try {
            const response = await getApprenticeshipOJTComparison(period);
            setPieData(response.data?.data || []);
        } catch (error) {
            console.error('Error fetching comparison:', error);
            setPieData([]);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            await Promise.all([
                fetchStats(),
                fetchCollegeHiring(hiringFilter),
                fetchComparison(pieFilter)
            ]);
            setLoading(false);
        };
        fetchData();
    }, [hiringFilter, pieFilter]);

    useEffect(() => {
        fetchCollegeHiring(hiringFilter);
    }, [hiringFilter]);

    useEffect(() => {
        fetchComparison(pieFilter);
    }, [pieFilter]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#F8FAFC', gap: 2 }}>
                <CircularProgress size={40} thickness={4} />
                <Typography variant="body2" color="#64748B" fontWeight="bold">Syncing Dashboard...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 5 }, bgcolor: '#F8FAFC', minHeight: '100vh' }}>
            {/* Header Section */}
            <Fade in={true} timeout={600}>
                <Box sx={{ mb: 5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                        <Typography variant="h4" fontWeight="1000" color="#0F172A" sx={{ letterSpacing: -1 }}>Strategic Overview</Typography>
                        <Typography variant="body2" color="#64748B" sx={{ mt: 0.5 }}>Real-time performance analytics for hiring and placements.</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: 'white', borderRadius: 3, border: '1px solid #E2E8F0' }}>
                        <CalendarIcon sx={{ fontSize: 18, color: '#64748B' }} />
                        <Typography variant="caption" fontWeight="bold" color="#475569">MARCH 2024</Typography>
                    </Box>
                </Box>
            </Fade>

            {/* Top Stats Row */}
            <Grid container spacing={4} sx={{ mb: 6 }}>
                <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        count={stats?.totalDrives || "0"}
                        title="Active Drives"
                        icon={<BusinessIcon />}
                        color="#6366F1"
                        delay={0}
                    />
                </Grid>
                <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        count={stats?.apprenticeshipCount || "0"}
                        title="Apprenticeships"
                        icon={<TrendingIcon />}
                        color="#10B981"
                        delay={100}
                    />
                </Grid>
                <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        count={stats?.ojtCount || "0"}
                        title="OJT Enrolment"
                        icon={<StudentIcon />}
                        color="#F59E0B"
                        delay={200}
                    />
                </Grid>
                <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <StatCard
                        count={stats?.collegesPartnered || "0"}
                        title="Partnered ITIs"
                        icon={<CollegeIcon />}
                        color="#EC4899"
                        delay={300}
                    />
                </Grid>
            </Grid>

            {/* Dashboard Content */}
            <Grid container spacing={4}>
                {/* Main Content Area */}
                <Grid item size={{ xs: 12, lg: 8 }}>
                    <Stack spacing={4}>
                        {/* Bar Chart Container */}
                        <Grow in={true} timeout={800}>
                            <Paper sx={{
                                p: 4,
                                borderRadius: 6,
                                border: '1px solid #F1F5F9',
                                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02), 0 20px 25px -5px rgba(0,0,0,0.03)'
                            }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                                    <Box>
                                        <Typography variant="h6" fontWeight="900" color="#0F172A">
                                            Penetration Analytics
                                        </Typography>
                                        <Typography variant="caption" color="#64748B">Engagement distribution across vocational clusters</Typography>
                                    </Box>
                                    <FormControl size="small">
                                        <Select
                                            value={hiringFilter}
                                            onChange={(e) => setHiringFilter(e.target.value)}
                                            sx={{
                                                fontWeight: 'bold',
                                                borderRadius: 3,
                                                bgcolor: '#F8FAFC',
                                                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2E8F0' },
                                                fontSize: '0.75rem'
                                            }}
                                        >
                                            <MenuItem value="this-month">Active Month</MenuItem>
                                            <MenuItem value="last-month">Previous Term</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>

                                <Box sx={{ height: 350, width: '100%' }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart
                                            data={barData}
                                            margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                                            <XAxis
                                                dataKey="collegeName"
                                                axisLine={false}
                                                tickLine={false}
                                                tick={{ fontSize: 10, fill: '#64748B', fontWeight: 600 }}
                                                interval={0}
                                                angle={-45}
                                                textAnchor="end"
                                                height={80}
                                            />
                                            <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94A3B8', fontSize: 11 }} />
                                            <RechartsTooltip content={<CustomTooltip />} />
                                            <Bar dataKey="apprenticeship" name="Apprenticeship" fill="#6366F1" radius={[6, 6, 0, 0]} barSize={20} />
                                            <Bar dataKey="ojt" name="OJT" fill="#F43F5E" radius={[6, 6, 0, 0]} barSize={20} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </Box>
                            </Paper>
                        </Grow>


                    </Stack>
                </Grid>

                {/* Side Panel Area */}
                <Grid item size={{ xs: 12, lg: 4 }}>
                    <Stack spacing={4} sx={{ height: '100%' }}>
                        <Grow in={true} timeout={1000}>
                            <Paper sx={{
                                p: 4,
                                borderRadius: 6,
                                flex: 1,
                                border: '1px solid #F1F5F9',
                                boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02), 0 20px 25px -5px rgba(0,0,0,0.03)'
                            }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                                    <Box>
                                        <Typography variant="h6" fontWeight="900" color="#0F172A">
                                            Structural Mix
                                        </Typography>
                                        <Typography variant="caption" color="#64748B">Programme split ratio</Typography>
                                    </Box>
                                    <IconButton size="small"><MoreIcon /></IconButton>
                                </Box>

                                <Box sx={{ height: 280, display: 'flex', justifyContent: 'center', alignItems: 'center', position: 'relative' }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={pieData || []}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={65}
                                                outerRadius={95}
                                                paddingAngle={8}
                                                dataKey="value"
                                            >
                                                {(pieData || []).map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.color || (index === 0 ? '#6366F1' : '#F43F5E')} />
                                                ))}
                                            </Pie>
                                            <RechartsTooltip content={<CustomTooltip />} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                    <Box sx={{ position: 'absolute', textAlign: 'center' }}>
                                        <Typography variant="h5" fontWeight="1000">100%</Typography>
                                        <Typography variant="caption" color="#64748B" fontWeight="bold">TOTAL</Typography>
                                    </Box>
                                </Box>

                                <Stack spacing={2} sx={{ mt: 4 }}>
                                    {(pieData || []).map((entry, i) => (
                                        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderRadius: 4, bgcolor: '#F8FAFC', border: '1px solid #F1F5F9' }}>
                                            <Stack direction="row" spacing={1.5} alignItems="center">
                                                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: entry.color }} />
                                                <Typography variant="body2" fontWeight="700" color="#475569">{entry.name}</Typography>
                                            </Stack>
                                            <Typography variant="body2" fontWeight="1000" color="#0F172A">{entry.value}</Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            </Paper>
                        </Grow>
                    </Stack>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Home;
